import PropTypes from "prop-types";
import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  DialogContentText,
  Button,
  IconButton,
  Typography,
  Stack,
  Box,
  Grid,
  CircularProgress,
  Divider,
  Card,
  useTheme,
  alpha,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import {
  Close,
  CameraEnhance,
  CurrencyRupeeOutlined,
  TextFieldsOutlined,
  Visibility,
  SearchOutlined,
  KeyboardVoice,
  Photo,
  Assistant,
  Download,
  Email,
  Phone,
  LocationOn,
} from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/slices/auth.slice";
import { useGetSponsoredUserUsageQuery, useStopSponsoredSubscriptionMutation } from "../../../redux/services/sponsorship.service";
import AppWidgetNew from "../app/AppWidgetNew";
import Label from "../../../components/label/Label";

UserUsageDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
    userId: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    contactNumber: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
    organizationName: PropTypes.string,
  }),
};

export default function UserUsageDialog({ open, onClose, user }) {
  const loggedInUser = useSelector(selectUser);
  const roles = loggedInUser?.roles?.map((r) => r.name) || [];
  const isOrganization = roles.includes("organization");
  const isSupport = roles.includes("support");
  const isOrgOrSupport = isOrganization || isSupport;

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [period, setPeriod] = useState("monthly");
  const [startDate, setStartDate] = useState(dayjs().subtract(1, "month"));
  const [endDate, setEndDate] = useState(dayjs());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [stopSubscription, { isLoading: isStopping }] = useStopSponsoredSubscriptionMutation();

  // Formulate params
  const params = useMemo(() => {
    const p = { period };
    if (period === "custom") {
      p.startDate = startDate && dayjs(startDate).isValid() ? dayjs(startDate).format("YYYY-MM-DD") : "";
      p.endDate = endDate && dayjs(endDate).isValid() ? dayjs(endDate).format("YYYY-MM-DD") : "";
    }
    return p;
  }, [period, startDate, endDate]);

  // Fetch usage only if dialog is open and user exists
  const { data, isLoading, isFetching, isError } = useGetSponsoredUserUsageQuery(
    { userId: user?.id, ...params },
    { skip: !open || !user?.id }
  );

  const usageData = data?.data?.usage || {};
  const cameraBased = usageData.cameraBasedFeatures || {};
  const nonCameraBased = usageData.nonCameraBasedFeatures || {};

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };

  const handleStartDateChange = (val) => {
    setStartDate(val);
    if (val && endDate && dayjs(val).isValid() && dayjs(endDate).isValid() && dayjs(val).isAfter(dayjs(endDate))) {
      setEndDate(val);
    }
  };

  const handleEndDateChange = (val) => {
    setEndDate(val);
    if (val && startDate && dayjs(val).isValid() && dayjs(startDate).isValid() && dayjs(val).isBefore(dayjs(startDate))) {
      setStartDate(val);
    }
  };

  const handleStopSubscription = () => {
    setConfirmOpen(true);
  };

  const confirmStopSubscription = async () => {
    try {
      const res = await stopSubscription(user.id).unwrap();
      if (res.success) {
        enqueueSnackbar(res.message || "Sponsored subscription stopped successfully.", { variant: "success" });
      } else {
        enqueueSnackbar(res.message || "Failed to stop subscription.", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar(err?.data?.message || "An error occurred while stopping subscription.", { variant: "error" });
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.customShadows?.z24,
        },
      }}
    >
      {/* Header Profile Section with Gradient Banner */}
      <Box
        sx={{
          py: 3,
          px: 3,
          position: "relative",
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: "common.white",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "common.white",
            "&:hover": {
              backgroundColor: alpha(theme.palette.common.white, 0.1),
            },
          }}
        >
          <Close />
        </IconButton>

        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              {isOrgOrSupport
                ? `User ID: ${user?.id || user?.userId || "--"}`
                : user?.name || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "User Usage Analytics"}
            </Typography>
            {data?.data?.sponsoredUser?.status && (
              <Label
                color={
                  data.data.sponsoredUser.status.toLowerCase() === "approved"
                    ? "success"
                    : data.data.sponsoredUser.status.toLowerCase() === "stopped"
                    ? "error"
                    : "warning"
                }
              >
                {data.data.sponsoredUser.status.toUpperCase()}
              </Label>
            )}
          </Stack>
          <Typography variant="subtitle1" sx={{ opacity: 0.85, fontWeight: "medium" }}>
            Sponsorship beneficiary at {user?.organizationName || "Voice Of SAP"}
          </Typography>

          {!isOrgOrSupport && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 3 }}
              sx={{ pt: 1, fontSize: "0.875rem", opacity: 0.9 }}
            >
              {user?.email && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Email fontSize="small" />
                  <Typography variant="body2">{user.email}</Typography>
                </Stack>
              )}
              {user?.contactNumber && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Phone fontSize="small" />
                  <Typography variant="body2">{user.contactNumber}</Typography>
                </Stack>
              )}
              {(user?.city || user?.state || user?.country) && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <LocationOn fontSize="small" />
                  <Typography variant="body2">
                    {[user.city, user.state, user.country].filter(Boolean).join(", ")}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DialogContent sx={{ p: 3, backgroundColor: "background.neutral" }}>
          {/* Filters Toolbar */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 3 }}
          >
            {/* Period Quick Selectors */}
            <Stack direction="row" spacing={1}>
              {[
                { label: "1W", value: "weekly" },
                { label: "1M", value: "monthly" },
                { label: "3M", value: "threeMonth" },
                { label: "1Y", value: "yearly" },
                { label: "Custom", value: "custom" },
              ].map((item) => (
                <Button
                  key={item.value}
                  variant={period === item.value ? "contained" : "outlined"}
                  onClick={() => handlePeriodChange(item.value)}
                  size="small"
                >
                  {item.label}
                </Button>
              ))}
            </Stack>

            {/* Custom Date Pickers */}
            {period === "custom" && (
              <Stack direction="row" spacing={2} alignItems="center">
                <DatePicker
                  label="Start Date"
                  format="DD/MM/YYYY"
                  disableFuture
                  maxDate={endDate && dayjs(endDate).isValid() && dayjs(endDate).isBefore(dayjs()) ? endDate : dayjs()}
                  value={startDate}
                  onChange={handleStartDateChange}
                  slotProps={{ textField: { size: "small" } }}
                />
                <Typography variant="body2">to</Typography>
                <DatePicker
                  label="End Date"
                  format="DD/MM/YYYY"
                  disableFuture
                  minDate={startDate && dayjs(startDate).isValid() ? startDate : undefined}
                  maxDate={dayjs()}
                  value={endDate}
                  onChange={handleEndDateChange}
                  slotProps={{ textField: { size: "small" } }}
                />
              </Stack>
            )}
          </Stack>

          {/* Loading & Content States */}
          {isLoading || isFetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={48} />
            </Box>
          ) : isError ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography variant="h6" color="error">
                Failed to load user usage analytics.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please check backend API connectivity and try again.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={4}>
              {/* Total Summary Row */}
              <Grid container spacing={3} justifyContent="center">
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      p: 3,
                      textAlign: "center",
                      background: `linear-gradient(135deg, ${alpha(theme.palette.success.light, 0.2)} 0%, ${alpha(
                        theme.palette.success.main,
                        0.1
                      )} 100%)`,
                      border: `1px solid ${theme.palette.success.light}`,
                    }}
                  >
                    <Typography variant="overline" color="success.main" sx={{ fontWeight: "bold" }}>
                      Overall Engagement
                    </Typography>
                    <Typography variant="h2" color="success.dark" sx={{ my: 1 }}>
                      {usageData.totalUsage || 0}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Features Utilized
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              {/* Camera Based Features Section */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" color="text.primary" sx={{ fontWeight: "bold" }}>
                    Camera-Based Features
                  </Typography>
                  <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: "semibold" }}>
                    Subtotal: {cameraBased.total || 0}
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <AppWidgetNew
                      title="Currency Mode"
                      total={cameraBased.totalCurrency || 0}
                      icon={<CurrencyRupeeOutlined />}
                      color="warning"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <AppWidgetNew
                      title="Text Mode"
                      total={cameraBased.textModeUsed || 0}
                      icon={<TextFieldsOutlined />}
                      color="info"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <AppWidgetNew
                      title="Smart Eye"
                      total={cameraBased.smartEyeUsed || 0}
                      icon={<Visibility />}
                      color="secondary"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <AppWidgetNew
                      title="Objects Detected"
                      total={cameraBased.totalObjects || 0}
                      icon={<SearchOutlined />}
                      color="error"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Non-Camera Based Features Section */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" color="text.primary" sx={{ fontWeight: "bold" }}>
                    Non-Camera-Based Features
                  </Typography>
                  <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: "semibold" }}>
                    Subtotal: {nonCameraBased.total || 0}
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <AppWidgetNew
                      title="MIRA Voice Mode"
                      total={nonCameraBased.miraUsed || 0}
                      icon={<KeyboardVoice />}
                      color="info"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <AppWidgetNew
                      title="Smart Image"
                      total={nonCameraBased.smartImageUsed || 0}
                      icon={<Photo />}
                      color="warning"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <AppWidgetNew
                      title="AI Tutor"
                      total={nonCameraBased.tutorUsed || 0}
                      icon={<Assistant />}
                      color="secondary"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <AppWidgetNew
                      title="Text to Audio"
                      total={nonCameraBased.textToAudioUsed || 0}
                      icon={<Download />}
                      color="primary"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          )}
        </DialogContent>
      </LocalizationProvider>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        {data?.data?.sponsoredUser?.status?.toLowerCase() === "approved" && isOrganization ? (
          <Button
            variant="contained"
            color="error"
            onClick={handleStopSubscription}
          >
            Stop Subscription
          </Button>
        ) : (
          <Box />
        )}
        <Button onClick={onClose} variant="contained" color="inherit">
          Close
        </Button>
      </DialogActions>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
      >
        <DialogTitle>Stop Sponsored Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ pt: 1 }}>
            Are you sure you want to stop the recurring sponsored subscription for <strong>{isOrgOrSupport ? (user?.id || user?.userId || "this user") : (user?.name || "this user")}</strong>? The user will keep their current active premium month until it expires, and no future months will be automatically sponsored.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">
            Cancel
          </Button>
          <LoadingButton
            onClick={confirmStopSubscription}
            color="error"
            variant="contained"
            loading={isStopping}
          >
            Stop Subscription
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
